import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSystemAdminStats } from '../services/api';
import { ADMIN_NAV } from '../components/adminMenu';
import '../styles/admin-ui.css';
import './Admin.css';

const statusChip = (endpoint) => {
  if (endpoint.agent_live) return { cls: 'signal', text: 'Live' };
  if (endpoint.status === 'online') return { cls: 'warn', text: 'Stale' };
  if (endpoint.status === 'maintenance') return { cls: 'warn', text: 'Maintenance' };
  return { cls: 'danger', text: 'Offline' };
};

const Admin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError('');
      try {
        const data = await getSystemAdminStats();
        if (!cancelled) setStats(data || null);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        if (!cancelled) {
          setStats(null);
          setStatsError(err.message || t('systemAdmin.statsError', 'Could not load live metrics'));
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [t]);

  const endpoints = useMemo(
    () => (Array.isArray(stats?.endpoints) ? stats.endpoints : []),
    [stats]
  );

  const menuItems = ADMIN_NAV.map((item) => {
    let badge = null;
    let stat;
    if (item.id === 'companies') stat = stats?.total_companies;
    if (item.id === 'servers') {
      stat = stats?.total_nodes;
      if (stats?.pending_nodes > 0) badge = `${stats.pending_nodes} pending`;
    }
    if (item.id === 'licenses') stat = undefined;
    if (item.id === 'logs') {
      stat = stats?.recent_logs_24h;
      if (stats?.error_logs_24h > 0) badge = `${stats.error_logs_24h} errors`;
    }
    if (item.id === 'tickets' && stats?.open_tickets != null) {
      stat = stats.open_tickets;
    }
    return {
      ...item,
      title: t(item.titleKey, item.title),
      description: t(item.descriptionKey, item.description),
      stat,
      badge,
    };
  });

  return (
    <div className="aui-page admin-hub">
      <div className="aui-hero">
        <div>
          <p className="aui-kicker">{t('systemAdmin.kicker', 'Control plane')}</p>
          <h1>{t('systemAdmin.title', 'System Admin')}</h1>
          <p className="aui-lead">
            {t(
              'systemAdmin.subtitle',
              'Live platform health — users, agents, endpoints, and operational signals.'
            )}
          </p>
          <p className="aui-lead-mobile">
            {t('systemAdmin.leadMobile', 'Platform health and admin modules.')}
          </p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={() => navigate('/admin/servers')}>
            {t('systemAdmin.openServers', 'Manage servers')}
          </button>
          <button type="button" className="aui-btn primary" onClick={() => navigate('/admin/logs')}>
            {t('systemAdmin.openLogs', 'View logs')}
          </button>
        </div>
      </div>

      <div className="aui-grid cards-3 admin-menu" aria-label={t('systemAdmin.menuLabel', 'Admin modules')}>
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className="aui-nav-tile"
            onClick={() => navigate(item.path)}
          >
            <span className="aui-nav-mark">{item.mark}</span>
            <strong>{item.title}</strong>
            <em>{item.description}</em>
            <div className="aui-nav-meta">
              {item.stat !== undefined && item.stat !== null && (
                <span className="aui-chip signal">{item.stat}</span>
              )}
              {item.badge && <span className="aui-chip warn">{item.badge}</span>}
              <span className="aui-chip">{t('systemAdmin.open', 'Open')}</span>
            </div>
          </button>
        ))}
      </div>

      {statsLoading ? (
        <div className="aui-stats">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <article key={n} className="aui-stat aui-stat-skeleton">
              <span>&nbsp;</span>
              <strong>&nbsp;</strong>
            </article>
          ))}
        </div>
      ) : stats ? (
        <div className="aui-stats admin-hub-stats">
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.users', 'Users')}</span>
            <strong>{stats.total_users ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.companies', 'Companies')}</span>
            <strong>{stats.total_companies ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.liveAgents', 'Live agents')}</span>
            <strong>{stats.connected_agents ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.servers', 'Servers online')}</span>
            <strong>
              {stats.online_nodes ?? 0}/{stats.total_nodes ?? 0}
            </strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.visible', 'Visible nodes')}</span>
            <strong>{stats.visible_nodes ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.connections', 'Active tunnels')}</span>
            <strong>{stats.active_connections ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.errors', 'Errors 24h')}</span>
            <strong>{stats.error_logs_24h ?? 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('systemAdmin.stats.openTickets', 'Open tickets')}</span>
            <strong>{stats.open_tickets ?? 0}</strong>
          </article>
        </div>
      ) : statsError ? (
        <p className="aui-inline-error">{statsError}</p>
      ) : null}

      <div className="aui-panel admin-hub-panel">
        <div className="admin-hub-panel-head">
          <div>
            <h2>{t('systemAdmin.endpoints.title', 'Endpoints')}</h2>
            <p>
              {t(
                'systemAdmin.endpoints.lead',
                'Truthful agent status from websocket + heartbeat — not stale DB flags.'
              )}
            </p>
          </div>
          <button type="button" className="aui-btn ghost" onClick={() => navigate('/admin/servers')}>
            {t('systemAdmin.endpoints.open', 'Open servers')}
          </button>
        </div>

        {statsLoading ? (
          <div className="aui-empty">
            <div className="aui-spinner" />
            <p>{t('systemAdmin.endpoints.loading', 'Loading endpoints…')}</p>
          </div>
        ) : endpoints.length === 0 ? (
          <div className="aui-empty">
            <h3>{t('systemAdmin.endpoints.empty', 'No endpoints yet')}</h3>
            <p>
              {t(
                'systemAdmin.endpoints.emptyLead',
                'Create a register key and onboard an agent to see nodes here.'
              )}
            </p>
          </div>
        ) : (
          <div className="aui-table-wrap">
            <table className="aui-table">
              <thead>
                <tr>
                  <th>{t('systemAdmin.endpoints.col.node', 'Node')}</th>
                  <th>{t('systemAdmin.endpoints.col.location', 'Location')}</th>
                  <th>{t('systemAdmin.endpoints.col.status', 'Status')}</th>
                  <th>{t('systemAdmin.endpoints.col.agent', 'Agent')}</th>
                  <th>{t('systemAdmin.endpoints.col.load', 'Load')}</th>
                  <th>{t('systemAdmin.endpoints.col.heartbeat', 'Last heartbeat')}</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((ep) => {
                  const chip = statusChip(ep);
                  return (
                    <tr key={ep.id}>
                      <td data-label={t('systemAdmin.endpoints.col.node', 'Node')}>
                        <strong>{ep.name}</strong>
                        {!ep.is_visible && (
                          <span className="aui-chip" style={{ marginLeft: 8 }}>
                            hidden
                          </span>
                        )}
                      </td>
                      <td data-label={t('systemAdmin.endpoints.col.location', 'Location')}>
                        {ep.flag_emoji} {ep.location || ep.country_code || '—'}
                      </td>
                      <td data-label={t('systemAdmin.endpoints.col.status', 'Status')}>
                        <span className={`aui-chip ${chip.cls}`}>{chip.text}</span>
                      </td>
                      <td data-label={t('systemAdmin.endpoints.col.agent', 'Agent')}>
                        {ep.agent_status || '—'}
                      </td>
                      <td data-label={t('systemAdmin.endpoints.col.load', 'Load')}>
                        {ep.load_percent ?? 0}%
                      </td>
                      <td data-label={t('systemAdmin.endpoints.col.heartbeat', 'Last heartbeat')}>
                        {ep.last_heartbeat
                          ? new Date(ep.last_heartbeat).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
